/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * DAS Edge Component
 * Renders connections between topology nodes as SVG paths with Bézier curves
 */

import React from "react";
import { TopologyEdge, NodePosition, TopologyNode } from "./types";
import { NODE_TYPE_CONFIG } from "./constants";

interface DasEdgeProps {
  edge: TopologyEdge;
  sourceNode: TopologyNode;
  targetNode: TopologyNode;
  sourcePos: NodePosition;
  targetPos: NodePosition;
  isHovered: boolean;
  onMouseEnter: (edgeId: string) => void;
  onMouseLeave: () => void;
}

export const DasEdge: React.FC<DasEdgeProps> = ({
  edge,
  sourceNode,
  targetNode,
  sourcePos,
  targetPos,
  isHovered,
  onMouseEnter,
  onMouseLeave,
}) => {
  const sourceConfig = NODE_TYPE_CONFIG[sourceNode.type];
  const targetConfig = NODE_TYPE_CONFIG[targetNode.type];

  // Calculate start and end points for horizontal (left-to-right) layout
  // Nodes are now centered at their position, so we connect from right edge to left edge
  // Start from right edge of source node
  const x1 = sourcePos.x + (sourceConfig?.width || 0) / 2;
  const y1 = sourcePos.y;

  // End at left edge of target node
  const x2 = targetPos.x - (targetConfig?.width || 0) / 2;
  const y2 = targetPos.y;

  // Calculate control points for cubic Bézier curve
  // Horizontal offset for control points creates smooth curve
  const controlPointOffset = Math.abs(x2 - x1) * 0.5;
  const cx1 = x1 + controlPointOffset;
  const cy1 = y1;
  const cx2 = x2 - controlPointOffset;
  const cy2 = y2;

  // Create SVG path with cubic Bézier
  const pathData = `M ${x1},${y1} C ${cx1},${cy1} ${cx2},${cy2} ${x2},${y2}`;

  // Mockup style: thinner lines with subtle colors
  const strokeWidth = isHovered ? 3.6 : 1.2;
  const strokeColor = isHovered ? "#f8fafc" : "#64748b"; // slate-50 / slate-500
  const opacity = isHovered ? 1 : 0.62;

  return (
    <g
      className="das-edge"
      onMouseEnter={() => onMouseEnter(edge.id)}
      onMouseLeave={onMouseLeave}
    >
      {/* Invisible wider path for easier hover */}
      <path
        d={pathData}
        stroke="transparent"
        strokeWidth={14}
        fill="none"
        style={{ cursor: "crosshair" }}
        pointerEvents="stroke"
      />
      
      {/* Visible path */}
      <path
        d={pathData}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        fill="none"
        opacity={opacity}
        pointerEvents="none"
        style={{
          transition: "stroke 0.2s, stroke-width 0.2s"
        }}
      />
    </g>
  );
};

/**
 * SVG marker definitions for arrow heads
 */
export const EdgeMarkers: React.FC = () => (
  <defs>
    <marker
      id="arrow"
      viewBox="0 0 10 10"
      refX="9"
      refY="5"
      markerWidth="6"
      markerHeight="6"
      orient="auto"
    >
      <path d="M 0 0 L 10 5 L 0 10 z" fill="#4b5563" opacity="0.4" />
    </marker>
    
    <marker
      id="arrow-hovered"
      viewBox="0 0 10 10"
      refX="9"
      refY="5"
      markerWidth="6"
      markerHeight="6"
      orient="auto"
    >
      <path d="M 0 0 L 10 5 L 0 10 z" fill="#60a5fa" />
    </marker>
  </defs>
);
